import React, { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import "bulma/css/bulma.min.css";

// Utility: clean and parse number strings like "12,345.67" -> number
const parseMoney = (s) => {
  if (s === null || s === undefined) return NaN;
  const cleaned = String(s).replace(/[,\s]/g, "");
  const n = parseFloat(cleaned);
  return isNaN(n) ? NaN : n;
};

export default function DynamicCatalogueChart({ dataObject }) {
  const { catalogueNames, filteredNames, yearSeries } = useMemo(() => {
    const namesArr = Array.isArray(dataObject["Catalogue Name"]) ? dataObject["Catalogue Name"] : [];
    const filtered = namesArr.filter((n) => {
      if (!n) return false;
      const lower = n.toString().toLowerCase().trim();
      if (lower === "statement catalogue summary" || lower === "total") return false;
      return true;
    });

    const years = Object.keys(dataObject)
      .filter((k) => k !== "Catalogue Name" && k.toString().trim() !== "Total")
      .filter((k) => /\d{4}/.test(k) || !/^[A-Za-z ]+$/.test(k))
      .sort();

    const series = years.map((yearKey) => {
      const row = { year: yearKey.trim() };
      const vals = Array.isArray(dataObject[yearKey]) ? dataObject[yearKey] : [];
      filtered.forEach((catalogue, idx) => {
        const origIndex = namesArr.findIndex((n) => n === catalogue);
        const valueStr = typeof vals[origIndex] !== "undefined" ? vals[origIndex] : vals[idx + 1] ?? "0";
        row[catalogue] = parseMoney(valueStr);
      });
      return row;
    });

    return { catalogueNames: namesArr, filteredNames: filtered, yearSeries: series };
  }, [dataObject]);

  const [selected, setSelected] = useState(filteredNames);
  const colors = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

  return (
    <div className="container mt-6 mb-6">
      <div >
        <div className="box">
          <center>
            <h2 className="title is-4">Catalogue Trends Over Years</h2>
            <div className="field">
              <label className="label">Select Catalogues</label>
              <div className="control">
                <div className="select is-multiple">
                  <select
                    multiple
                    value={selected}
                    onChange={(e) => {
                      const options = Array.from(e.target.selectedOptions).map((o) => o.value);
                      setSelected(options);
                    }}
                  >
                    {filteredNames.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </center>
          <div style={{ height: "360px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={yearSeries} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip formatter={(value) => (typeof value === "number" ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value)} />
                <Legend />
                {selected.map((cat, idx) => (
                  <Line
                    key={cat}
                    type="monotone"
                    dataKey={cat}
                    stroke={colors[idx % colors.length]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
